"use client";

import * as React from "react";
import { useMediaQuery } from "usehooks-ts";

// Single source of truth for breakpoint values, shared with the Tailwind
// `screens` config via `@jasperlabs/ui/breakpoints` so the two can never drift.
import breakpointValues from "../../lib/breakpoints.json";

export enum Breakpoint {
	XS = "xs",
	SM = "sm",
	MD = "md",
	LG = "lg",
	XL = "xl",
}

export const breakpoints: Record<Breakpoint, string> = breakpointValues;

function getMediaQuery({
	from,
	to,
}: {
	from?: Breakpoint;
	to?: Breakpoint;
}): string {
	if (to && !from) {
		return `(min-width: ${breakpoints[to]})`;
	}
	if (from && !to) {
		return `(max-width: calc(${breakpoints[from]} - 1px))`;
	}
	if (from && to) {
		return `(min-width: ${breakpoints[from]}) and (max-width: ${breakpoints[to]})`;
	}
	return "";
}

/** Renders children only while the viewport matches the `from`/`to` breakpoint window. */
export const Hidden = ({
	children,
	from,
	to,
}: {
	from?: Breakpoint;
	to?: Breakpoint;
	children: React.ReactNode;
}) => {
	const matchesQuery = useMediaQuery(getMediaQuery({ from, to }));
	return matchesQuery ? <React.Fragment>{children}</React.Fragment> : null;
};

/**
 * Returns the current breakpoint. Built on `useMediaQuery` (matching {@link Hidden})
 * so it reads the real viewport on mount rather than starting from a `width === 0`
 * default, which would resolve to `XS` and flash the wrong content before hydration.
 */
export function useBreakpoint(): Breakpoint {
	const isSm = useMediaQuery(`(min-width: ${breakpoints.sm})`);
	const isMd = useMediaQuery(`(min-width: ${breakpoints.md})`);
	const isLg = useMediaQuery(`(min-width: ${breakpoints.lg})`);
	const isXl = useMediaQuery(`(min-width: ${breakpoints.xl})`);

	if (isXl) {
		return Breakpoint.XL;
	}
	if (isLg) {
		return Breakpoint.LG;
	}
	if (isMd) {
		return Breakpoint.MD;
	}
	if (isSm) {
		return Breakpoint.SM;
	}
	return Breakpoint.XS;
}

function decrementBreakpoint(breakpoint: Breakpoint): Breakpoint {
	switch (breakpoint) {
		case Breakpoint.XL:
			return Breakpoint.LG;
		case Breakpoint.LG:
			return Breakpoint.MD;
		case Breakpoint.MD:
			return Breakpoint.SM;
		case Breakpoint.SM:
			return Breakpoint.XS;
		case Breakpoint.XS:
			return Breakpoint.XS;
		default: {
			const _exhaustive: never = breakpoint;
			return _exhaustive;
		}
	}
}

function valueAtBreakpoint<T>(
	breakpoint: Breakpoint,
	values: Partial<Record<Breakpoint, T>>,
): T {
	if (breakpoint in values) {
		return values[breakpoint]!;
	}
	return valueAtBreakpoint(decrementBreakpoint(breakpoint), values);
}

/** Returns the value for the current breakpoint, falling back to the nearest smaller breakpoint. */
export function useValueAtBreakpoint<T>(
	values: Partial<Record<Breakpoint, T>>,
): T {
	return valueAtBreakpoint(useBreakpoint(), values);
}
